import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { taskColumnKeys } from './taskColumnKeys';
import { taskKeys } from './taskKeys';
import {
  TaskColumnData,
  FormattedTaskColumn,
  CreateTaskColumnInput,
  UpdateTaskColumnInput,
  ReorderTaskColumnInput,
  formatTaskColumn,
} from './taskColumnTypes';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import {
  invalidateQuery,
  safeUpdateQueryData,
  batchInvalidateQueries,
} from '../../utils/queryInvalidationUtils';
import { COLUMN_COLORS } from '../../constants/colorConstants';

/**
 * Hook for fetching task columns for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useTaskColumns(topicId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? taskColumnKeys.byTopic(topicId) : taskColumnKeys.lists(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(
        `Starting task columns fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`
      );
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching task columns from IndexedDB #${fetchId}`);

          try {
            // Get task columns from IndexedDB
            const taskColumns = await db.taskColumns
              .where('topic_id')
              .equals(topicId)
              .and(column => !column.isDeleted) // Filter out deleted columns
              .toArray();

            // Sort by position
            taskColumns.sort((a, b) => a.position - b.position);

            logger.fetch(`Offline mode: loaded ${taskColumns.length} task columns from IndexedDB`);

            // Format columns using the helper function
            return taskColumns.map(column => formatTaskColumn(column as TaskColumnData));
          } catch (offlineError) {
            logger.error(`Error fetching offline task columns #${fetchId}:`, offlineError);
            throw new Error('Failed to load task columns from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('task_columns')
          .select('*')
          .eq('topic_id', topicId)
          .order('position');

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          if (data && data.length > 0) {
            await db.taskColumns.bulkPut(data);
            logger.fetch(`Stored ${data.length} task columns in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error storing task columns in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format columns using the helper function
        const formattedColumns = (data || []).map(column =>
          formatTaskColumn(column as TaskColumnData)
        );

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Task columns fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedColumns;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchTaskColumns',
            topicId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!topicId,
  });
}

/**
 * Hook for fetching a single task column by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useTaskColumn(columnId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: columnId ? taskColumnKeys.detail(columnId) : taskColumnKeys.details(),
    queryFn: async () => {
      if (!columnId) {
        throw new Error('Column ID is required');
      }

      logger.fetch(`Fetching task column ${columnId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching task column ${columnId} from IndexedDB`);

          try {
            // Get column from IndexedDB
            const column = await db.taskColumns.get(columnId);

            if (!column || column.isDeleted) {
              throw new Error(`Task column ${columnId} not found in offline storage`);
            }

            // Format the column
            return formatTaskColumn(column as TaskColumnData);
          } catch (offlineError) {
            logger.error(`Error fetching offline task column ${columnId}:`, offlineError);
            throw new Error('Failed to load task column from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('task_columns')
          .select('*')
          .eq('id', columnId)
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.taskColumns.put(data);
        } catch (storageError) {
          logger.error(`Error storing task column in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the column
        return formatTaskColumn(data as TaskColumnData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchTaskColumn', columnId, isOnline },
        });
        throw appError;
      }
    },
    enabled: !!columnId,
  });
}

/**
 * Hook for creating a new task column
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddTaskColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (columnInput: CreateTaskColumnInput): Promise<FormattedTaskColumn> => {
      const { topicId, name = 'New Column', color, afterColumnId } = columnInput;

      logger.store('Adding new task column', {
        topicId,
        afterColumnId,
        is_online: isOnline,
      });

      // Generate unique status ID for the new column
      const statusId = `custom_${Date.now().toString(36)}`;

      // Use provided color or pick a random one
      const columnColor = color || COLUMN_COLORS[Math.floor(Math.random() * COLUMN_COLORS.length)];

      // If offline, we need to manually calculate position
      if (!isOnline) {
        try {
          // Get existing columns for this topic to calculate position
          const existingColumns = await db.taskColumns
            .where('topic_id')
            .equals(topicId)
            .and(col => !col.isDeleted)
            .toArray();

          // Sort by position
          existingColumns.sort((a, b) => a.position - b.position);

          // Calculate new position
          let newPosition: number;

          if (afterColumnId) {
            // Find the position after the specified column
            const afterColumn = existingColumns.find(col => col.id === afterColumnId);

            if (afterColumn) {
              // Find the next column after this one
              const afterIndex = existingColumns.findIndex(col => col.id === afterColumnId);
              const nextColumn =
                afterIndex < existingColumns.length - 1 ? existingColumns[afterIndex + 1] : null;

              if (nextColumn) {
                // Position between the two columns
                newPosition =
                  afterColumn.position + (nextColumn.position - afterColumn.position) / 2;
              } else {
                // Position after the last column
                newPosition = afterColumn.position + 1000;
              }
            } else {
              // If specified column not found, add at the end
              newPosition =
                existingColumns.length > 0
                  ? existingColumns[existingColumns.length - 1].position + 1000
                  : 1000;
            }
          } else {
            // If no afterColumnId, add at the end
            newPosition =
              existingColumns.length > 0
                ? existingColumns[existingColumns.length - 1].position + 1000
                : 1000;
          }

          // Create column data object with metadata
          const now = new Date().toISOString();
          const columnId = crypto.randomUUID();

          // Create column without metadata field
          const columnData: TaskColumnData = {
            id: columnId,
            topic_id: topicId,
            name,
            color: columnColor,
            status: statusId,
            position: newPosition,
            created_at: now,
            updated_at: now,
          };

          // Store in IndexedDB
          await db.taskColumns.put(columnData);

          // Queue for sync when online
          await queueOfflineOperation('taskColumns', 'create', columnId, columnData);

          // Return formatted column
          return formatTaskColumn(columnData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'createTaskColumn', topicId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - use database function to insert and rebalance
      try {
        logger.component('Adding new column using database function', {
          topicId,
          afterColumnId,
          statusId,
          color: columnColor,
        });

        const { error } = await supabase.rpc('insert_column_at_position', {
          p_topic_id: topicId,
          p_name: name,
          p_color: columnColor,
          p_status: statusId,
          p_after_column_id: afterColumnId || null,
        });

        if (error) throw error;

        // Query for the newly created column to get its ID and full data
        const { data: newColumns, error: fetchError } = await supabase
          .from('task_columns')
          .select('*')
          .eq('topic_id', topicId)
          .eq('status', statusId)
          .limit(1);

        if (fetchError) throw fetchError;

        if (!newColumns || newColumns.length === 0) {
          throw new Error('Failed to retrieve newly created column');
        }

        const newColumn = newColumns[0] as TaskColumnData;

        // Store in IndexedDB for offline access
        try {
          await db.taskColumns.put(newColumn);
        } catch (storageError) {
          logger.error(`Error storing task column in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Return formatted column
        return formatTaskColumn(newColumn);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'createTaskColumn', topicId },
        });
        throw appError;
      }
    },

    onSuccess: (newColumn, variables) => {
      const { topicId } = variables;

      // Invalidate columns list for the topic
      invalidateQuery(queryClient, taskColumnKeys.byTopic(topicId), {
        refetchType: 'active',
        context: { operation: 'createTaskColumn', columnId: newColumn.id },
      });

      // Add the new column to the cache
      safeUpdateQueryData(queryClient, taskColumnKeys.detail(newColumn.id), () => newColumn, {
        context: { operation: 'createTaskColumn', columnId: newColumn.id },
      });
    },
  });
}

/**
 * Hook for updating a task column
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateTaskColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      columnId,
      updates,
    }: {
      columnId: string;
      updates: UpdateTaskColumnInput;
    }) => {
      if (!columnId) {
        throw new Error('Column ID is required');
      }

      const now = new Date().toISOString();

      // Create a filtered updates object with only valid fields
      const filteredUpdates: Record<string, unknown> = {
        updated_at: now,
      };

      if (updates.name !== undefined) filteredUpdates.name = updates.name;
      if (updates.color !== undefined) filteredUpdates.color = updates.color;

      // Skip metadata for now as the column doesn't exist
      // and everything was working without it

      // If offline, update locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Updating task column locally', { columnId });

        try {
          // Get current column from IndexedDB
          const currentColumn = await db.taskColumns.get(columnId);

          if (!currentColumn || currentColumn.isDeleted) {
            throw new Error(`Task column ${columnId} not found in offline storage`);
          }

          // Get the topic ID for cache updates
          const topicId = currentColumn.topic_id;

          // Merge updates with current data
          const updatedColumn = {
            ...currentColumn,
            ...filteredUpdates,
          };

          // Update in IndexedDB
          await db.taskColumns.put(updatedColumn);

          // Queue for sync when online
          await queueOfflineOperation('taskColumns', 'update', columnId, updatedColumn);

          return { columnId, topicId, updates: filteredUpdates, timestamp: now };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTaskColumn', columnId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        // Get current column to identify its topic for cache updates
        const { data: currentColumn, error: fetchError } = await supabase
          .from('task_columns')
          .select('topic_id')
          .eq('id', columnId)
          .single();

        if (fetchError) throw fetchError;

        const topicId = currentColumn.topic_id;

        // Update the column
        const { data, error } = await supabase
          .from('task_columns')
          .update(filteredUpdates)
          .eq('id', columnId)
          .select()
          .single();

        if (error) throw error;

        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingColumn = await db.taskColumns.get(columnId);

          if (existingColumn) {
            await db.taskColumns.put({
              ...existingColumn,
              ...data,
            });
          }
        } catch (storageError) {
          logger.error(`Error updating task column in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { columnId, topicId, updates: filteredUpdates, timestamp: now, data };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateTaskColumn', columnId },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      const { columnId, topicId, data } = result;

      // Update column detail cache if it exists
      const updateSuccessful = safeUpdateQueryData<FormattedTaskColumn>(
        queryClient,
        taskColumnKeys.detail(columnId),
        currentColumn => {
          if (!currentColumn) return undefined;

          // If we have data from the server, use that for formatting
          if (data) {
            return formatTaskColumn(data as TaskColumnData);
          }

          // Otherwise use the updates
          return {
            ...currentColumn,
            ...(result.updates.name && { name: result.updates.name as string }),
            ...(result.updates.color && { color: result.updates.color as string }),
            updatedAt: result.timestamp,
          };
        },
        { context: { operation: 'updateTaskColumn', columnId } }
      );

      // If detail was updated, also update in lists
      if (updateSuccessful && topicId) {
        // Update column in topic lists
        safeUpdateQueryData<FormattedTaskColumn[]>(
          queryClient,
          taskColumnKeys.byTopic(topicId),
          columns => {
            if (!columns) return undefined;

            return columns.map(column => (column.id === columnId ? updateSuccessful : column));
          },
          { context: { operation: 'updateTaskColumn', columnId } }
        );
      } else if (topicId) {
        // If the column isn't in cache or update failed, invalidate queries to refetch
        const queryKeysToInvalidate = new Map([
          [taskColumnKeys.detail(columnId), { exact: true }],
          [taskColumnKeys.byTopic(topicId), { exact: true }],
        ]);

        batchInvalidateQueries(queryClient, queryKeysToInvalidate, {
          refetchType: 'active', // Only refetch if someone is watching
        });
      }
    },
  });
}

/**
 * Hook for deleting a task column
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteTaskColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (columnId: string) => {
      try {
        // Get column data first to know its topic_id and status for cache updates and task deletion
        const column = !isOnline
          ? await db.taskColumns.get(columnId)
          : (await supabase.from('task_columns').select('*').eq('id', columnId).single()).data;

        if (!column) {
          throw new Error('Column not found');
        }

        const topicId = column.topic_id;
        const status = column.status;

        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking task column as deleted locally', { columnId });

          try {
            // Mark tasks in this column as deleted (assuming tasks table exists in IndexedDB)
            try {
              const tasksTable = db.table('tasks');
              const tasksToDelete = await tasksTable.where('status').equals(status).toArray();

              // Mark each task as deleted and queue deletion operations
              for (const task of tasksToDelete) {
                await tasksTable.update(task.id, { isDeleted: true });
                await queueOfflineOperation('tasks', 'delete', task.id, { id: task.id });
              }
            } catch (tasksError) {
              logger.error('Error marking tasks as deleted in offline mode:', tasksError);
              // Continue with column deletion even if task deletion fails
            }

            // Mark column as deleted in IndexedDB
            await db.taskColumns.update(columnId, { isDeleted: true });

            // Queue for sync when online
            await queueOfflineOperation('taskColumns', 'delete', columnId, { id: columnId });

            return { columnId, topicId, status };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { operation: 'deleteTaskColumn', columnId, mode: 'offline' },
            });
            throw error;
          }
        }

        // Online flow - delete tasks first, then the column
        logger.component('Deleting tasks in column', {
          columnId,
          status,
        });

        const { error: tasksError } = await supabase.from('tasks').delete().eq('status', status);

        if (tasksError) {
          logger.error('Failed to delete tasks in column', {
            error: tasksError,
            columnId,
          });
          // Continue with column deletion even if task deletion fails
        }

        // Delete the column
        logger.component('Deleting column', { columnId });
        const { error } = await supabase.from('task_columns').delete().eq('id', columnId);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await db.taskColumns.delete(columnId);

          // Also try to remove associated tasks if IndexedDB has a tasks table
          try {
            const tasksTable = db.table('tasks');
            await tasksTable.where('status').equals(status).delete();
          } catch (deleteTasksError) {
            // If tasks table doesn't exist yet, this is fine - just log and continue
            logger.error('Error deleting tasks from IndexedDB:', deleteTasksError);
          }
        } catch (storageError) {
          logger.error(`Error deleting task column from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { columnId, topicId, status };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteTaskColumn', columnId },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      const { columnId, topicId } = result;

      if (!topicId) {
        logger.error('Missing topicId in deleteTaskColumn success handler');
        return;
      }

      // Remove column from cache
      queryClient.removeQueries({
        queryKey: taskColumnKeys.detail(columnId),
        exact: true,
      });

      // Update the columns list in the cache by filtering out the deleted column
      safeUpdateQueryData<FormattedTaskColumn[]>(
        queryClient,
        taskColumnKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return oldData;
          return oldData.filter(column => column.id !== columnId);
        },
        { context: { operation: 'deleteTaskColumn', columnId, topicId } }
      );

      // Invalidate any task queries that might be affected by the deletion
      queryClient.invalidateQueries({
        queryKey: taskKeys.byTopic(topicId),
        exact: false,
      });
    },
  });
}

/**
 * Hook for reordering a task column
 * Uses the database function to handle the reordering
 */
export function useReorderTaskColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, columnId, newIndex }: ReorderTaskColumnInput) => {
      logger.component('Reordering task column', {
        topicId,
        columnId,
        newIndex,
        isOnline,
      });

      // If offline, we need to manually rebalance positions
      if (!isOnline) {
        try {
          // Get all columns for this topic
          const columns = await db.taskColumns
            .where('topic_id')
            .equals(topicId)
            .and(col => !col.isDeleted)
            .toArray();

          // Sort by position
          columns.sort((a, b) => a.position - b.position);

          // Find the column we're moving
          const columnToMove = columns.find(c => c.id === columnId);
          if (!columnToMove) {
            throw new Error(`Column ${columnId} not found`);
          }

          // Make sure the newIndex is within bounds
          const targetPosition = Math.min(Math.max(0, newIndex), columns.length - 1);

          // Remove the column from its current position
          const filteredColumns = columns.filter(c => c.id !== columnId);

          // Insert it at the new position
          filteredColumns.splice(targetPosition, 0, columnToMove);

          // Update positions for all columns (use 1000-unit spacing)
          // Skip the metadata field since it doesn't exist in the database
          const updatedColumns = filteredColumns.map((col, index) => ({
            ...col,
            position: (index + 1) * 1000,
            updated_at: new Date().toISOString(),
          }));

          // Update all columns in IndexedDB
          await Promise.all(updatedColumns.map(col => db.taskColumns.put(col)));

          // Queue operation for the moved column
          const movedColumn = updatedColumns.find(c => c.id === columnId);
          if (movedColumn) {
            await queueOfflineOperation('taskColumns', 'update', columnId, movedColumn);
          }

          return { topicId, columnId, newIndex, success: true };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'reorderTaskColumn', columnId, topicId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - use database function to move the column and rebalance
      try {
        // Make sure the newIndex is within bounds - adding 1 because DB function uses 1-based positions
        const targetPosition = newIndex + 1;

        logger.component('Reordering column using database function', {
          columnId,
          targetPosition,
        });

        const { error } = await supabase.rpc('move_column_to_position', {
          p_column_id: columnId,
          p_target_position: targetPosition,
        });

        if (error) throw error;

        // Fetch updated columns to get all correct positions
        const { data: updatedColumns, error: fetchError } = await supabase
          .from('task_columns')
          .select('*')
          .eq('topic_id', topicId)
          .order('position');

        if (fetchError) throw fetchError;

        // Update IndexedDB for offline access
        try {
          if (updatedColumns && updatedColumns.length > 0) {
            await db.taskColumns.bulkPut(updatedColumns);
          }
        } catch (storageError) {
          logger.error(`Error updating task columns in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { topicId, columnId, newIndex, success: true };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'reorderTaskColumn', columnId, topicId },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      // Invalidate the topic's column list to refresh with new positions
      invalidateQuery(queryClient, taskColumnKeys.byTopic(result.topicId), {
        refetchType: 'active',
        context: { operation: 'reorderTaskColumn', columnId: result.columnId },
      });
    },
  });
}

// Removed the useTaskColumnSubscription hook - use the dedicated implementation in useTaskColumnSubscription.ts
